import { useEffect, useRef } from 'react';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $generateHtmlFromNodes, $generateNodesFromDOM } from '@lexical/html';
import { $getRoot, $insertNodes, $setSelection } from 'lexical';
import { sanitizeHtml } from '@utils/strings/sanitizeHtml';


/** Allowed tags for the editor. All other tags will be removed. Add/remove more tags as needed. */
export const allowedTags = [
  // block level
  'p', 'ol', 'ul', 'li', 'hr', 'blockquote', 'span', 'a', 'br',
  // text formatting
  'em', 'i', 'strong', 'b', 'strike', 'sub', 'sup', 'b', 'i', 'u', 's',
  // list
  'ol', 'ul', 'li',
  // table
  'table', 'caption', 'thead', 'tbody', 'tfoot', 'colgroup', 'col', 'tr', 'td', 'th',
  // media
  'img', 'video',
];

/** Allowed attributes for the editor. All other attributes will be removed. Add/remove more attributes as needed. */
export const allowedAttributes = [
  // image attributes
  'src', 'srcset', 'alt', 'title', 'width', 'height',
];


/** HtmlPlugin props */
interface HtmlPluginProps {
  initialHtml?: string;
  onHtmlChanged?: (html: string) => void;
  onTextChanged?: (text: string) => void;
  autoFocus?: boolean;
}


/**
 * @description set the editors default value, and get the modified html content
 * for getting the modified html content, the callback function **onHtmlChanged** is used
 * we use the **OnChangePlugin** to get the modified html content
 * - `initialHtml` - the editors initial value. The string will be sanitized before being set as the editors value.
 * - `onHtmlChanged` - the callback function to get the modified html content (string with tags)
 * - `onTextChanged` - the callback function to get the modified text content (string without tags)
 * - `autoFocus` - if the editor should be focused on load; default true
 */
const HtmlPlugin = (props: HtmlPluginProps) => {
  // Destructure props
  const {
    initialHtml,
    onHtmlChanged,
    onTextChanged,
    autoFocus = true,
  } = props;

  const [editor] = useLexicalComposerContext();

  // To keep track of the last HTML string
  const lastInitialHtmlRef = useRef(initialHtml);
  const isFirstLoad = useRef(true);

  useEffect(() => {
    if ((!isFirstLoad.current && initialHtml === lastInitialHtmlRef.current) || initialHtml === undefined) {
      return;
    }

    // Ensure this runs only once on first load or when `initialHtml` changes
    editor.update(() => {
      const parser = new DOMParser();
      // Sanitize the html; normally db should be trusted, but just in case, because at the moment there's
      // no sanitization on the backend
      const dom = parser.parseFromString(sanitizeHtml(initialHtml, { ALLOWED_TAGS: allowedTags }), 'text/html');
      const nodes = $generateNodesFromDOM(editor, dom);
      $getRoot().clear();
      $insertNodes(nodes);
      // Prevent autofocus on the initial load
      if (!autoFocus) $setSelection(null);
    });

    // Update lastInitialHtmlRef with the latest processed HTML
    lastInitialHtmlRef.current = initialHtml;

    // Mark that the initial load has occurred
    isFirstLoad.current = false;
  }, [editor, initialHtml, autoFocus]);

  return (
    <OnChangePlugin
      onChange={(editorState) => {
        editorState.read(() => {
          if (onHtmlChanged) onHtmlChanged(sanitizeHtml($generateHtmlFromNodes(editor), { ALLOWED_TAGS: allowedTags, ALLOWED_ATTR: allowedAttributes }));
          if (onTextChanged) onTextChanged(editor.read(() => $getRoot().getTextContent().trim()));
        });
      }}
    />
  );
};

export default HtmlPlugin;
